import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import GavelIcon from '@material-ui/icons/Gavel';
import BusinessIcon from '@material-ui/icons/Business';
import HomeIcon from '@material-ui/icons/Home';
import PeopleIcon from '@material-ui/icons/People';
import SecurityIcon from '@material-ui/icons/Security';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import { Typography } from '@material-ui/core';
import { practiceAreasDisplay } from '../../data/pageData';

const styles = (theme) => ({
  section: {
    padding: '60px 0',
    width: '100%',
    boxSizing: 'border-box',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '60px',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  sectionNumber: {
    fontSize: '14px',
    letterSpacing: '3px',
    color: '#d4145a',
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: 600,
    marginBottom: '15px',
  },
  sectionTitleText: {
    fontSize: '48px',
    marginTop: '15px',
    marginBottom: '20px',
    fontWeight: 700,
    color: '#1a1a1a',
    fontFamily: '"Georgia", serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '32px',
    },
  },
  sectionDescription: {
    fontSize: '18px',
    color: '#555',
    maxWidth: '700px',
    margin: '0 auto',
    fontFamily: '"Segoe UI", sans-serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '30px',
    marginBottom: '30px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  featureBox: {
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    padding: '30px',
    transition: 'all 0.3s',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: '#333',
    cursor: 'pointer',
    '&:hover': {
      background: '#1e3a5f',
      color: 'white',
      boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
      borderColor: '#1e3a5f',
    },
    '&:hover $featureIcon': {
      color: 'white',
    },
    '&:hover $featureTitle': {
      color: 'white',
    },
    '&:hover $featureTitle:after': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
    '&:hover $featureDescription': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '25px',
    color: '#1e3a5f',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      transition: 'all 0.3s',
    },
  },
  featureTitle: {
    fontSize: '24px',
    marginBottom: '15px',
    color: '#1e3a5f',
    transition: 'all 0.3s',
    fontWeight: 600,
    fontFamily: '"Georgia", serif',
    position: 'relative',
    paddingBottom: '8px',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '40px',
      height: '2px',
      background: 'rgba(30, 58, 95, 0.3)',
      transition: 'all 0.3s',
    },
  },
  featureDescription: {
    fontSize: '15px',
    lineHeight: 1.7,
    fontFamily: '"Segoe UI", sans-serif',
    color: '#555',
    transition: 'all 0.3s',
  },
  caseTypesList: {
    margin: '15px 0 0 0',
    padding: 0,
    listStyle: 'none',
  },
  caseTypeItem: {
    padding: '8px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  caseTypeText: {
    fontSize: '14px',
    color: '#e0e0e0',
    fontFamily: '"Segoe UI", sans-serif',
  },
  practiceTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '20px',
  },
  practiceTag: {
    background: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    padding: '5px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: '600',
    transition: 'all 0.3s',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
      color: 'white',
      cursor: 'pointer',
    },
  }
});

function PracticeAreasSection(props) {
  const { classes } = props;
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Map practice area keys to their respective icons
  const practiceAreaIcons = {
    litigation: GavelIcon,
    corporate: BusinessIcon,
    property: HomeIcon,
    family: PeopleIcon,
    criminal: SecurityIcon,
    constitutional: AccountBalanceIcon
  };


  // Modified onClick handler to navigate to the practice area page
  const handlePracticeClick = (event, path) => {
    event.stopPropagation();
    navigate(path);
    window.scrollTo(0, 0);
  };

  // Handle hover expand/collapse
  const handleMouseEnter = (index) => {
    setExpandedIndex(index);
  };
  
  const handleMouseLeave = () => {
    setExpandedIndex(null);
  };

  return (
    <section className={classes.section} id="practice-areas">
      <div className={classes.sectionHeader}>
        <div className={classes.sectionNumber}>II. PRACTICE AREAS</div>
        <Typography variant="h2" className={classes.sectionTitleText}>
          Our Practice Areas
        </Typography>
        <Typography className={classes.sectionDescription}>
          We offer specialized expertise across key legal domains in the Indian judicial system
        </Typography>
      </div>
        
      <div className={classes.featureGrid}>
        {Object.entries(practiceAreasDisplay).map(([key, area], index) => {
          const IconComponent = practiceAreaIcons[key];
          return (
            <div 
              className={classes.featureBox} 
              key={index}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              <div className={classes.featureIcon}>
                <IconComponent fontSize="large" />
              </div>
            <Typography variant="h3" className={classes.featureTitle}>
              {area.title}
            </Typography>
            <Typography className={classes.featureDescription} style={{ marginTop: '15px' }}>
              {area.description}
            </Typography>
            
            <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
              <div className={classes.practiceTags}>
                {area.areas.map((practice, i) => (
                  <div 
                    key={i} 
                    className={classes.practiceTag}
                    onClick={(e) => handlePracticeClick(e, practice.path)}
                  >
                    {practice.name}
                  </div>
                ))}
              </div>
            </Collapse>
          </div>
          );
        })}
      </div>
    </section>
  );
}

export default withStyles(styles)(PracticeAreasSection);